/** "35 min ago"-style label (e.g. the Reports Home Recent list). */
export function relativeTimeLabel(iso: string | undefined): string {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const minutes = Math.floor((Date.now() - then) / 60_000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return hours === 1 ? '1 hour ago' : `${hours} hours ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days} days ago`;
  const weeks = Math.floor(days / 7);
  return weeks === 1 ? '1 week ago' : `${weeks} weeks ago`;
}

const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

/**
 * A localized "2 hr. ago" / "yesterday" / "Sep 15" label. Under an hour reads in minutes and
 * under a day in hours; past that, the previous calendar day reads as "yesterday" and
 * anything older as a short month-and-day date, since a relative "12 days ago" is harder
 * to place than the date itself.
 *
 * @param locale a BCP 47 tag, e.g. the app's primary `intl` locale
 * @param now epoch ms; injectable so a label can be computed deterministically
 * @returns '' for a missing or unparseable timestamp; a future one reads as "now"
 */
export function localizedRelativeTime(
  iso: string | undefined,
  locale: string,
  now: number = Date.now()
): string {
  if (!iso) return '';
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return '';

  // 'short', not 'narrow': fr-CA's narrow form renders as a bare "-2 h".
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto', style: 'short' });
  const elapsed = Math.max(0, now - then.getTime());
  if (elapsed < MINUTE_MS) return rtf.format(0, 'second');
  if (elapsed < HOUR_MS) return rtf.format(-Math.floor(elapsed / MINUTE_MS), 'minute');
  if (elapsed < DAY_MS) return rtf.format(-Math.floor(elapsed / HOUR_MS), 'hour');

  const today = new Date(now);
  const yesterday = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1);
  const sameDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();
  if (sameDay(then, yesterday)) return rtf.format(-1, 'day');

  return new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric' }).format(then);
}
